import type { CustomerSummary, OrderStatus } from '@horevia/contracts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  OrderStatusBadge,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ClipboardList, Search, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PERIOD_PRESETS,
  resolvePeriod,
  todayIsoDate,
  type PeriodPreset,
  type PeriodRange,
} from '@/features/analytics/period';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { useSession } from '@/features/auth/session';
import { CustomerSearchField } from '@/features/customers/CustomerSearchField';
import { useCurrentOutlet } from '@/features/pos/outlet';
import { ordersQueryOptions, ordersSummaryQueryOptions } from '@/features/pos/queries';
import { ORDER_STATUSES, orderTone, preparationTone } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime } from '@/lib/format';

interface OrdersSearch {
  status?: OrderStatus;
  q?: string;
  /** Commandes rattachées à ce client (Horevia CRM, ADR-025). */
  customerId?: string;
  period?: PeriodPreset;
  from?: string;
  to?: string;
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/pos/orders/')({
  validateSearch: (search: Record<string, unknown>): OrdersSearch => ({
    ...(ORDER_STATUSES.includes(search['status'] as OrderStatus)
      ? { status: search['status'] as OrderStatus }
      : {}),
    ...(typeof search['q'] === 'string' && search['q'] ? { q: search['q'] } : {}),
    ...(typeof search['customerId'] === 'string' && UUID.test(search['customerId'])
      ? { customerId: search['customerId'] }
      : {}),
    ...(PERIOD_PRESETS.includes(search['period'] as PeriodPreset)
      ? { period: search['period'] as PeriodPreset }
      : {}),
    ...(typeof search['from'] === 'string' && ISO_DATE.test(search['from'])
      ? { from: search['from'] }
      : {}),
    ...(typeof search['to'] === 'string' && ISO_DATE.test(search['to'])
      ? { to: search['to'] }
      : {}),
  }),
  component: OrdersPage,
});

/** Historique des commandes du point de vente : synthèse de la période, filtres, liste paginée. */
function OrdersPage() {
  const { t, i18n } = useTranslation(['pos', 'analytics', 'common', 'errors', 'crm']);
  const session = useSession();
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { outlet, outlets, setOutletId } = useCurrentOutlet();
  const canRead = session.can('order.read');

  const today = todayIsoDate(session.user.tenant.timezone);
  const preset: PeriodPreset = search.period ?? 'TODAY';
  const custom: PeriodRange = { from: search.from ?? today, to: search.to ?? today };
  const range = resolvePeriod(preset, today, custom);
  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const setSearch = (next: Partial<OrdersSearch>) =>
    void navigate({
      search: (current) => {
        const merged: Record<string, unknown> = { ...current, ...next };
        for (const key of Object.keys(merged)) if (merged[key] === undefined) delete merged[key];
        return merged;
      },
      replace: true,
    });

  const filters = {
    outletId: outlet?.id,
    status: search.status,
    customerId: search.customerId,
    from: range.from,
    to: range.to,
    q: debounced || undefined,
  };
  const query = useInfiniteQuery(ordersQueryOptions(filters, 25, canRead && Boolean(outlet)));
  const summary = useQuery(
    ordersSummaryQueryOptions(
      { outletId: outlet?.id, from: range.from, to: range.to },
      canRead && Boolean(outlet),
    ),
  );
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const currency = summary.data?.currencyCode ?? session.user.tenant.currencyCode;

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('pos:orders.title')}
        subtitle={t('pos:orders.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:orders.title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {outlets.length > 1 && outlet ? (
              <select
                className="select"
                aria-label={t('pos:bar.outlet')}
                value={outlet.id}
                onChange={(event) => setOutletId(event.target.value)}
              >
                {outlets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : null}
            <PeriodPicker
              preset={preset}
              onPresetChange={(next) => setSearch({ period: next, from: undefined, to: undefined })}
              range={range}
              custom={custom}
              onCustomChange={(next) =>
                setSearch({ period: 'CUSTOM', from: next.from, to: next.to })
              }
              hideCompare
            />
          </div>
        }
      />

      <div className="grid grid-4">
        <KpiCard
          label={t('pos:orders.kpi.revenue')}
          value={formatAmount(summary.data?.revenue ?? 0, i18n.language, currency)}
          tone="green"
          icon={<TrendingUp className="ico" aria-hidden="true" />}
          loading={summary.isPending}
        />
        <KpiCard
          label={t('pos:orders.kpi.paid')}
          value={summary.data?.paidCount ?? 0}
          tone="violet"
          loading={summary.isPending}
        />
        <KpiCard
          label={t('pos:orders.kpi.average')}
          value={formatAmount(summary.data?.averageTicket ?? 0, i18n.language, currency)}
          tone="blue"
          loading={summary.isPending}
        />
        <KpiCard
          label={t('pos:orders.kpi.open')}
          value={summary.data?.openCount ?? 0}
          tone="amber"
          loading={summary.isPending}
        />
      </div>

      {summary.data && summary.data.topProducts.length > 0 ? (
        <Card>
          <CardHeader title={t('pos:orders.topProducts')} />
          <CardBody density="flush">
            <TableWrap>
              <Table compact>
                <tbody>
                  {summary.data.topProducts.slice(0, 5).map((product) => (
                    <Tr key={product.productId ?? product.label}>
                      <Td>{product.label}</Td>
                      <Td numeric align="right">
                        {product.quantity}
                      </Td>
                      <Td numeric align="right">
                        {formatAmount(product.amount, i18n.language, currency)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </CardBody>
        </Card>
      ) : null}

      <Toolbar>
        <Input
          aria-label={t('pos:orders.search')}
          placeholder={t('pos:orders.search')}
          icon={<Search className="ico" aria-hidden="true" />}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        {session.can('customer.read') ? (
          <div style={{ minWidth: 220 }}>
            <CustomerSearchField
              label={t('crm:pos.filterCustomer')}
              placeholder={t('crm:pos.filterCustomer')}
              value={customer}
              onChange={(value) => {
                setCustomer(value);
                setSearch({ customerId: value?.id });
              }}
            />
          </div>
        ) : null}
        <ChipFilter active={!search.status} onClick={() => setSearch({ status: undefined })}>
          {t('pos:orders.allStatuses')}
        </ChipFilter>
        {ORDER_STATUSES.filter((status) => status !== 'TRANSFERRED').map((status) => (
          <ChipFilter
            key={status}
            active={search.status === status}
            onClick={() => setSearch({ status })}
          >
            {t(`pos:status.${status}`)}
          </ChipFilter>
        ))}
      </Toolbar>

      {query.isPending && outlet ? (
        <TableSkeleton rows={6} columns={7} label={t('common:states.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="ico" aria-hidden="true" />}
          title={t('pos:orders.empty')}
          description={t('pos:orders.emptyHint')}
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>{t('pos:orders.columns.number')}</Th>
                  <Th>{t('pos:orders.columns.reference')}</Th>
                  <Th>{t('pos:orders.columns.table')}</Th>
                  <Th>{t('pos:orders.columns.status')}</Th>
                  <Th>{t('pos:orders.columns.preparation')}</Th>
                  <Th align="right">{t('pos:orders.columns.total')}</Th>
                  <Th align="right">{t('pos:orders.columns.balance')}</Th>
                  <Th>{t('pos:orders.columns.server')}</Th>
                  <Th>{t('pos:orders.columns.openedAt')}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((order) => (
                  <Tr key={order.id}>
                    <Td strong>#{order.dailyNumber}</Td>
                    <Td>
                      <Link
                        to="/pos/orders/$orderId"
                        params={{ orderId: order.id }}
                        className="mono"
                      >
                        {order.reference}
                      </Link>
                      {order.stayReservationId ? (
                        <div className="t-12 muted">
                          <Link
                            to="/reservations/$reservationId"
                            params={{ reservationId: order.stayReservationId }}
                          >
                            {t('pos:orders.stayShort', { reference: order.stayReference ?? '' })}
                          </Link>
                        </div>
                      ) : null}
                    </Td>
                    <Td>
                      {order.tableCode ??
                        (order.tabName
                          ? t('pos:ticket.tab', { name: order.tabName })
                          : t(`pos:serviceType.${order.serviceType}`))}
                    </Td>
                    <Td>
                      <OrderStatusBadge tone={orderTone(order.status)}>
                        {t(`pos:status.${order.status}`)}
                      </OrderStatusBadge>
                    </Td>
                    <Td>
                      <OrderStatusBadge tone={preparationTone(order.preparationStatus)}>
                        {t(`pos:preparation.${order.preparationStatus}`)}
                      </OrderStatusBadge>
                    </Td>
                    <Td numeric align="right">
                      {formatAmount(order.total, i18n.language, order.currencyCode)}
                    </Td>
                    <Td numeric align="right">
                      {order.balance > 0
                        ? formatAmount(order.balance, i18n.language, order.currencyCode)
                        : '—'}
                    </Td>
                    <Td>{order.serverName ?? '—'}</Td>
                    <Td>{formatDateTime(order.openedAt, i18n.language)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          {query.hasNextPage ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:actions.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Container>
  );
}
